import React, { useState, useEffect } from 'react';
import { getBillingStatus, getBillingPlans, createCheckoutSession } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { 
  CreditCard, Check, Zap, Rocket, 
  ShieldCheck, Loader2, ExternalLink, 
  Calendar, AlertCircle
} from 'lucide-react';
import { toast } from 'sonner';

export default function AdminSubscription() {
  const [status, setStatus] = useState(null);
  const [plans, setPlans] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isRedirecting, setIsRedirecting] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setIsLoading(true);
    try {
      const [statusData, plansData] = await Promise.all([
        getBillingStatus(),
        getBillingPlans()
      ]);
      setStatus(statusData);
      setPlans(plansData);
    } catch {
      toast.error('Daten konnten nicht geladen werden');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubscribe = async (planId) => {
    setIsRedirecting(true);
    try {
      const { url } = await createCheckoutSession(planId);
      window.open(url, '_blank');
      toast.info('Sie wurden zu Stripe weitergeleitet');
    } catch {
      toast.error('Fehler beim Erstellen der Sitzung');
    } finally {
      setIsRedirecting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <Loader2 className="w-8 h-8 animate-spin text-[#1a2f38]" />
      </div>
    );
  }

  const currentPlan = plans.find(p => p.id === status?.plan_id) || { name: 'Kein aktives Abo' };
  const getStatusColor = (s) => {
    switch(s) {
      case 'active': return 'bg-emerald-100 text-emerald-700 border-emerald-200';
      case 'trial': return 'bg-blue-100 text-blue-700 border-blue-200';
      case 'past_due': return 'bg-rose-100 text-rose-700 border-rose-200';
      default: return 'bg-slate-100 text-slate-500 border-slate-200';
    }
  };

  return (
    <div className="p-6 space-y-8 animate-in fade-in duration-500">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-[#1a2f38]">Abonnement & Abrechnung</h1>
          <p className="text-slate-500 text-sm">Verwalten Sie Ihre Tarife und Rechnungen</p>
        </div>
      </div>

      {/* Current Status Overview */}
      <Card className="shadow-lg border-[#1a2f38]/5 overflow-hidden">
        <div className="bg-gradient-to-r from-[#1a2f38] to-[#2a4048] p-6 text-white flex flex-col md:flex-row md:items-center justify-between gap-6">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-2xl bg-white/10 backdrop-blur-md flex items-center justify-center border border-white/20">
              <CreditCard className="w-7 h-7 text-emerald-400" />
            </div>
            <div>
              <p className="text-white/60 text-sm font-medium">Aktueller Tarif</p>
              <h2 className="text-2xl font-bold">{currentPlan.name}</h2>
            </div>
          </div>
          
          <div className="flex flex-wrap gap-4">
            <div className={`px-4 py-2 rounded-xl border text-sm font-bold flex items-center gap-2 ${getStatusColor(status?.subscription_status)}`}>
              <div className={`w-2 h-2 rounded-full ${status?.subscription_status === 'active' ? 'bg-emerald-500' : 'bg-slate-400'}`} />
              {status?.subscription_status === 'active' ? 'Aktiv' : 
               status?.subscription_status === 'trial' ? 'Testphase' : 'Inaktiv'}
            </div>
            {status?.current_period_end && (
              <div className="px-4 py-2 rounded-xl bg-white/10 backdrop-blur-md border border-white/20 text-sm font-bold flex items-center gap-2">
                <Calendar className="w-4 h-4 text-emerald-400" />
                Nächste Abrechnung: {new Date(status.current_period_end).toLocaleDateString()}
              </div>
            )}
          </div>
        </div>
        
        {status?.subscription_status === 'inactive' && (
          <div className="p-4 bg-rose-50 border-b border-rose-100 flex items-center gap-3 text-rose-700">
            <AlertCircle className="w-5 h-5" />
            <p className="font-semibold text-sm">Warnung: Ohne aktives Abonnement ist der POS-Betrieb eingeschränkt.</p>
          </div>
        )}
      </Card>

      {/* Plans Section */}
      <div>
        <h2 className="text-3xl font-bold text-[#1a2f38] mb-8 text-center pt-4">Verfügbare Tarife</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-6xl mx-auto">
          {plans.map((plan) => (
            <Card key={plan.id} className={`group relative flex flex-col transition-all hover:scale-[1.03] hover:shadow-2xl active:scale-95 duration-300 border-2 
              ${status?.plan_id === plan.id ? 'border-emerald-500 shadow-xl' : 'border-slate-100 shadow-sm'}`}>
              
              {status?.plan_id === plan.id && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-emerald-500 text-white text-[10px] font-black uppercase px-4 py-1 rounded-full shadow-lg z-10">
                  Ihr aktueller Tarif
                </div>
              )}
              
              <CardHeader className="text-center pb-2">
                <div className="w-12 h-12 rounded-xl bg-slate-50 flex items-center justify-center mx-auto mb-4 group-hover:bg-[#1a2f38] group-hover:text-white transition-colors duration-300">
                  {plan.id === 'basic' ? <Zap className="w-6 h-6" /> : 
                   plan.id === 'pro' ? <Rocket className="w-6 h-6" /> : <ShieldCheck className="w-6 h-6" />}
                </div>
                <CardTitle className="text-xl font-black">{plan.name}</CardTitle>
                <div className="mt-2 flex items-baseline justify-center gap-1">
                  <span className="text-4xl font-black">€{plan.price}</span>
                  <span className="text-slate-400 text-sm font-bold">/Monat</span>
                </div>
              </CardHeader>
              
              <CardContent className="flex-1 flex flex-col space-y-6">
                <div className="space-y-3 pt-4">
                  {plan.features.map((feat, i) => (
                    <div key={i} className="flex items-center gap-3 text-sm font-bold text-slate-600">
                      <div className="w-5 h-5 rounded-full bg-emerald-50 flex items-center justify-center shrink-0">
                        <Check className="w-3 h-3 text-emerald-500" />
                      </div>
                      {feat}
                    </div>
                  ))}
                </div>
                
                <Button 
                  onClick={() => handleSubscribe(plan.id)}
                  disabled={status?.plan_id === plan.id || isRedirecting}
                  className={`w-full h-12 rounded-xl font-bold text-base transition-all mt-auto
                    ${status?.plan_id === plan.id 
                      ? 'bg-slate-100 text-slate-400' 
                      : 'bg-[#1a2f38] hover:bg-[#2a4048] shadow-lg hover:shadow-emerald-500/20 active:scale-95'}`}
                >
                  {status?.plan_id === plan.id ? 'Aktuell' : 'Tarif wählen'}
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>

      {/* Invoice History (Mock) */}
      <Card className="shadow-sm border-slate-100">
        <CardHeader>
          <CardTitle className="text-lg">Letzte Rechnungen</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead>
                <tr className="border-b text-slate-400 font-bold uppercase text-[10px] tracking-wider">
                  <th className="pb-3">Datum</th>
                  <th className="pb-3">Rechnungsnummer</th>
                  <th className="pb-3 text-right">Betrag</th>
                  <th className="pb-3 text-right">Status</th>
                  <th className="pb-3 text-right">PDF</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {[
                  { date: '2026-03-01', id: 'INV-2026-001', amount: '€89.00', status: 'Bezahlt' },
                  { date: '2026-02-01', id: 'INV-2026-002', amount: '€89.00', status: 'Bezahlt' }
                ].map((inv, i) => (
                  <tr key={i} className="group hover:bg-slate-50 transition-colors">
                    <td className="py-4 font-medium text-slate-600">{new Date(inv.date).toLocaleDateString()}</td>
                    <td className="py-4 font-mono font-medium">{inv.id}</td>
                    <td className="py-4 text-right font-bold">{inv.amount}</td>
                    <td className="py-4 text-right">
                      <span className="px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 text-xs font-bold">{inv.status}</span>
                    </td>
                    <td className="py-4 text-right">
                      <button className="p-1 px-3 rounded-lg border border-slate-200 hover:bg-white hover:shadow-sm text-slate-400 hover:text-[#1a2f38] transition-all">
                        <ExternalLink className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
